import React from 'react';
import { C } from '../../data/constants';

export function NudgeLogo({ size = 'md', white = false }) {
  const sizes = { sm: { font: 18, sub: 14 }, md: { font: 22, sub: 18 }, lg: { font: 28, sub: 24 } };
  const s = sizes[size] || sizes.md;
  return (
    <span style={{ fontFamily: "'DM Serif Display', serif", display: 'inline-flex', alignItems: 'baseline', gap: 1 }}>
      <span style={{ color: white ? '#02c39a' : C.teal, fontSize: s.font, fontWeight: 400 }}>Nudge</span>
      <span style={{ color: white ? '#ffffff' : C.navy, fontSize: s.sub, fontWeight: 700, fontFamily: "'DM Sans', sans-serif" }}>AI</span>
    </span>
  );
}

export function Card({ children, style = {}, className = '' }) {
  return (
    <div
      className={className}
      style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: 28,
        ...style
      }}
    >
      {children}
    </div>
  );
}

export function PrimaryBtn({ children, onClick, disabled = false, style = {}, fullWidth = false }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: disabled ? '#94a3b8' : C.teal,
        color: '#fff',
        border: 'none',
        borderRadius: 8,
        padding: '10px 22px',
        fontSize: 14,
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        width: fullWidth ? '100%' : 'auto',
        fontFamily: "'DM Sans', sans-serif",
        ...style,
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = '#0b8278'; }}
      onMouseLeave={e => { if (!disabled) e.currentTarget.style.background = C.teal; }}
    >
      {children}
    </button>
  );
}

export function SecondaryBtn({ children, onClick, disabled = false, style = {} }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: 'transparent',
        color: C.text,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        padding: '10px 22px',
        fontSize: 14,
        fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: "'DM Sans', sans-serif",
        ...style,
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.borderColor = '#94a3b8'; }}
      onMouseLeave={e => { if (!disabled) e.currentTarget.style.borderColor = C.border; }}
    >
      {children}
    </button>
  );
}

export function ProgressBar({ current, total, label, color = C.teal }) {
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;
  return (
    <div>
      {label && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 12, color: C.muted, fontWeight: 500 }}>{label}</span>
          <span style={{ fontSize: 12, color: C.muted }}>{current} of {total}</span>
        </div>
      )}
      <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: 'hidden' }}>
        <div
          className="progress-fill"
          style={{ height: '100%', width: `${pct}%`, borderRadius: 3 }}
        />
      </div>
    </div>
  );
}

export function SectionHeader({ step, total, title }) {
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 10,
      background: 'rgba(250,251,252,0.96)',
      backdropFilter: 'blur(8px)',
      borderBottom: `1px solid ${C.border}`,
      padding: '12px 24px',
    }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 13, color: C.muted, fontWeight: 500 }}>
            {title}
          </span>
          <span style={{ fontSize: 12, color: C.muted }}>{step} of {total}</span>
        </div>
        <ProgressBar current={step} total={total} />
      </div>
    </div>
  );
}

export function SelectButton({ label, selected, onClick, style = {} }) {
  return (
    <button
      onClick={onClick}
      className="select-btn"
      style={{
        padding: '10px 16px',
        borderRadius: 8,
        border: selected ? `1.5px solid ${C.teal}` : `1px solid ${C.border}`,
        background: selected ? C.tealLight : '#fff',
        color: selected ? C.teal : C.text,
        fontWeight: selected ? 600 : 400,
        fontSize: 14,
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
        fontFamily: "'DM Sans', sans-serif",
        ...style,
      }}
    >
      {label}
    </button>
  );
}

export function TimeBadge({ text, color }) {
  const bgColor = color + '22';
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: 20,
      background: bgColor,
      color: color,
      fontSize: 11,
      fontWeight: 600,
      whiteSpace: 'nowrap',
      fontFamily: "'DM Sans', sans-serif",
    }}>
      {text}
    </span>
  );
}

export function Navbar({ page, setPage, onJoinStudy }) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const navLinks = ['home','mission','about','disclaimers','contact'];

  return (
    <nav style={{
      position: 'sticky', top: 0, zIndex: 100,
      background: 'rgba(250,251,252,0.95)',
      backdropFilter: 'blur(8px)',
      borderBottom: `1px solid ${C.border}`,
      height: 56,
      display: 'flex', alignItems: 'center',
    }}>
      <div style={{
        maxWidth: 1100, margin: '0 auto', width: '100%',
        padding: '0 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <button
          onClick={() => { setPage('home'); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          <NudgeLogo size="md" />
        </button>

        {/* Desktop nav */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 28 }} className="hide-mobile">
            {navLinks.map(p => (
              <button
                key={p}
                onClick={() => setPage(p)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 13, fontWeight: 500,
                  color: page === p ? C.teal : C.body,
                  textTransform: 'capitalize', padding: '4px 0',
                  borderBottom: page === p ? `2px solid ${C.teal}` : '2px solid transparent',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {p}
              </button>
            ))}
            <a
              href="/admin"
              style={{
                fontSize: 13, fontWeight: 500,
                color: C.muted,
                textDecoration: 'none',
                padding: '4px 0',
                borderBottom: '2px solid transparent',
                fontFamily: "'DM Sans', sans-serif",
              }}
              onMouseEnter={e => e.currentTarget.style.color = C.body}
              onMouseLeave={e => e.currentTarget.style.color = C.muted}
            >
              Admin
            </a>
            <PrimaryBtn onClick={onJoinStudy} style={{ padding: '7px 18px', fontSize: 13 }}>
              Join Study
            </PrimaryBtn>
          </div>

        {/* Mobile hamburger */}
        <button
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'none' }}
          className="show-mobile"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <div style={{ width: 22, height: 2, background: C.text, marginBottom: 5 }} />
          <div style={{ width: 22, height: 2, background: C.text, marginBottom: 5 }} />
          <div style={{ width: 22, height: 2, background: C.text }} />
        </button>
      </div>

      {menuOpen && (
        <div style={{
          position: 'absolute', top: 56, left: 0, right: 0,
          background: '#fff', borderBottom: `1px solid ${C.border}`,
          padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
        }}>
          {navLinks.map(p => (
            <button
              key={p}
              onClick={() => { setPage(p); setMenuOpen(false); }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 14, fontWeight: 500, textAlign: 'left',
                color: page === p ? C.teal : C.text,
                textTransform: 'capitalize',
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              {p}
            </button>
          ))}
          <PrimaryBtn onClick={() => { onJoinStudy(); setMenuOpen(false); }} fullWidth>
            Join Study
          </PrimaryBtn>
        </div>
      )}
    </nav>
  );
}

export function Footer() {
  return (
    <footer style={{ background: C.navy, padding: '28px 24px', marginTop: 'auto' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <NudgeLogo size="md" white />
          <span style={{ color: C.muted, fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>nudgeai.ai</span>
        </div>
        <a
          href="/admin"
          style={{
            fontSize: 12,
            color: 'rgba(255,255,255,0.35)',
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 500,
            textDecoration: 'none',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 6,
            padding: '5px 12px',
            letterSpacing: '0.04em',
          }}
          onMouseEnter={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.75)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.35)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.35)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
        >
          Researcher Login
        </a>
      </div>
    </footer>
  );
}
